import React, { useState, useEffect, useRef, useCallback } from 'react';
import EditorImagem from './EditorImagem';
import { useAuth } from '../contexts/AuthContext';
import { getSocket, useSocket } from '../hooks/useSocket';
import api from '../services/api';
import toast from 'react-hot-toast';
import EmojiPicker from 'emoji-picker-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import './Atendimento.css';

// Som de notificação via Web Audio API
// Inicializar AudioContext na primeira interação


export default function Atendimento() {
  const { usuario, canais } = useAuth();
  const token = localStorage.getItem('token');
  useSocket(token);
  const [canalAtivo, setCanalAtivo] = useState(canais[0] || null);
  const [fila, setFila] = useState([]);
  const [painelRecolhido, setPainelRecolhido] = useState(() => {
    try { return localStorage.getItem('painelFilaRecolhido') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('painelFilaRecolhido', painelRecolhido ? '1' : '0'); } catch {}
  }, [painelRecolhido]);
  const [atendimentoAtivo, setAtendimentoAtivo] = useState(null);
  const setAtendimentoAtivoSync = setAtendimentoAtivo;
  const [mensagens, setMensagens] = useState([]);
  const [texto, setTexto] = useState('');
  const [showEmoji, setShowEmoji] = useState(false);
  const [showRespostasRapidas, setShowRespostasRapidas] = useState(false);
  const [respostasRapidas, setRespostasRapidas] = useState([]);
  const [novaResposta, setNovaResposta] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [transferirModal, setTransferirModal] = useState(false);
  const [atendentes, setAtendentes] = useState([]);
  const [editandoNome, setEditandoNome] = useState(false);
  const [novoNome, setNovoNome] = useState('');
  const [editandoNumero, setEditandoNumero] = useState(false);
  const [novoNumero, setNovoNumero] = useState('');
  const messagesEndRef = useRef(null);
  const chamarAtencao = useCallback((mensagemTitulo) => {
    // Só chama atenção se a aba/janela não estiver em foco (ex: sistema aberto em outro monitor)
    if (document.hasFocus()) return;
    // Piscar título da aba
    if (tituloIntervalRef.current) clearInterval(tituloIntervalRef.current);
    let piscando = false;
    tituloIntervalRef.current = setInterval(() => {
      document.title = piscando ? tituloOriginalRef.current : `🔴 ${mensagemTitulo}`;
      piscando = !piscando;
    }, 1000);
    const pararPiscar = () => {
      clearInterval(tituloIntervalRef.current);
      tituloIntervalRef.current = null;
      document.title = tituloOriginalRef.current;
      window.removeEventListener('focus', pararPiscar);
    };
    window.addEventListener('focus', pararPiscar);
    // Flash visual na tela
    setTelaFlash(true);
    setTimeout(() => setTelaFlash(false), 900);
  }, []);
  const atendimentoAtivoRef = useRef(null);
  useEffect(() => {
    atendimentoAtivoRef.current = atendimentoAtivo;
  }, [atendimentoAtivo]);

  useEffect(() => {
    const init = () => {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      if (audioCtxRef.current.state === 'suspended') audioCtxRef.current.resume();
    };
    document.addEventListener('click', init);
    return () => document.removeEventListener('click', init);
  }, []);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [gravando, setGravando] = useState(false);
  const [audioPendente, setAudioPendente] = useState(null); // { blob, url, file }

  const iniciarConversa = async () => {
    if (!novaConversa.numero.trim()) return toast.error('Informe o número');
    if (!canalAtivo) return toast.error('Selecione um canal');
    setIniciando(true);
    try {
      const { data } = await api.post('/atendimentos/iniciar', {
        canal_id: canalAtivo.id,
        numero: novaConversa.numero,
        nome: novaConversa.nome,
      });
      setModalNovaConversa(false);
      setNovaConversa({ nome: '', numero: '' });
      toast.success('Conversa iniciada!');
      // Selecionar o atendimento diretamente
      try {
        const fRes = await api.get(`/canais/${canalAtivo.id}/fila`);
        setFila(fRes.data);
        const at = fRes.data.find(a => a.id === data.atendimento.id);
        const atObj = at || {
          ...data.atendimento,
          cliente_nome: data.cliente.nome_whatsapp || data.cliente.numero_whatsapp,
          numero_whatsapp: data.cliente.numero_whatsapp,
          cliente_id: data.cliente.id,
          status: 'em_atendimento',
          atendente_id: usuario.id,
        };
        const msgs = await api.get(`/atendimentos/${data.atendimento.id}/mensagens`);
        setMensagens(msgs.data);
        setAtendimentoAtivoSync(atObj);
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 200);
      } catch(e) { console.error(e); }
    } catch(err) {
      toast.error(err.response?.data?.erro || 'Erro ao iniciar conversa');
    } finally { setIniciando(false); }
  };

  const tocarSom = useCallback((tipo = 'mensagem') => {
    try {
      if (tipo === 'fila') {
        // Pop discreto estilo Slack/Teams via Web Audio API
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const gain = ctx.createGain();
        gain.connect(ctx.destination);
        // Primeira nota: pop suave
        const osc1 = ctx.createOscillator();
        osc1.type = 'sine';
        osc1.connect(gain);
        osc1.frequency.setValueAtTime(880, ctx.currentTime);
        osc1.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.08);
        gain.gain.setValueAtTime(0.0, ctx.currentTime);
        gain.gain.linearRampToValueAtTime(0.18, ctx.currentTime + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.18);
        osc1.start(ctx.currentTime);
        osc1.stop(ctx.currentTime + 0.18);
        // Segunda nota: leve eco descendente
        const osc2 = ctx.createOscillator();
        const gain2 = ctx.createGain();
        osc2.type = 'sine';
        osc2.connect(gain2);
        gain2.connect(ctx.destination);
        osc2.frequency.setValueAtTime(660, ctx.currentTime + 0.12);
        gain2.gain.setValueAtTime(0.0, ctx.currentTime + 0.12);
        gain2.gain.linearRampToValueAtTime(0.10, ctx.currentTime + 0.14);
        gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.32);
        osc2.start(ctx.currentTime + 0.12);
        osc2.stop(ctx.currentTime + 0.32);
        osc2.onended = () => ctx.close();
      } else {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.setValueAtTime(660, ctx.currentTime);
        gain.gain.setValueAtTime(0.2, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.2);
        osc.onended = () => ctx.close();
      }
    } catch(e) {}
  }, []);
  const audioCtxRef = useRef(null);
  const [respondendo, setRespondendo] = useState(null);
  const [presenceCliente, setPresenceCliente] = useState(null); // mensagem sendo respondida
  const [modalNovaConversa, setModalNovaConversa] = useState(false);
  const [buscaFila, setBuscaFila] = useState('');
  const [novaConversa, setNovaConversa] = useState({ nome: '', numero: '' });
  const [iniciando, setIniciando] = useState(false);
  const [menuContexto, setMenuContexto] = useState(null); // {msg, x, y}
  const [naoLidas, setNaoLidas] = useState({}); // { atendimento_id: count }
  const [imagemParaEditar, setImagemParaEditar] = useState(null);
  const [aviso, setAviso] = useState({ aviso_ativo: false, aviso_texto: '' });
  const [telaFlash, setTelaFlash] = useState(false);
  const tituloOriginalRef = useRef(document.title);
  const tituloIntervalRef = useRef(null);
  const [showAvisoEditor, setShowAvisoEditor] = useState(false);
  const [avisoTextoEdit, setAvisoTextoEdit] = useState('');

  const [editorFerramenta, setEditorFerramenta] = useState('retangulo');
  const [editorCor, setEditorCor] = useState('#e53935');
  const [editorTextoInput, setEditorTextoInput] = useState('');
  const [editorTextoPos, setEditorTextoPos] = useState(null);
  const canvasEditorRef = useRef(null);
  const editorDrawingRef = useRef(false);
  const editorStartRef = useRef({ x: 0, y: 0 });
  const editorShapesRef = useRef([]);
  const editorImgRef = useRef(null);
  const [editandoMsg, setEditandoMsg] = useState(null);
  const editandoMsgRef = useRef(null);
  const [textoEdicao, setTextoEdicao] = useState('');
  const menuContextoRef = useRef(null);
  const atendenteNomesRef = useRef({});

  // Separa fila por status
  const aguardando = fila.filter(a => a.status === 'aguardando');
  const emAtendimento = fila.filter(a => a.status === 'em_atendimento');
  const encerrados = fila.filter(a => a.status === 'encerrado' && (!buscaFila || (a.cliente_nome || '').toLowerCase().includes(buscaFila.toLowerCase())));

  const formatarNumero = (numero) => {
    if (!numero) return '';
    if (numero.startsWith('lid_')) return '';
    let n = numero.replace(/[^0-9]/g, '');
    if (n.length === 10 || n.length === 11) n = '55' + n;
    if (n.length > 13) return '';
    if (n.length === 13 && n.startsWith('55'))
      return `+${n.slice(0,2)} (${n.slice(2,4)}) ${n.slice(4,5)} ${n.slice(5,9)}-${n.slice(9)}`;
    if (n.length === 12 && n.startsWith('55'))
      return `+${n.slice(0,2)} (${n.slice(2,4)}) ${n.slice(4,8)}-${n.slice(8)}`;
    return numero;
  };

  const agruparPorCliente = (lista) => {
    const porCliente = new Map();
    const prioridade = { em_atendimento: 3, aguardando: 2, encerrado: 1 };
    for (const a of lista) {
      const chave = a.cliente_id || a.numero_whatsapp;
      const existente = porCliente.get(chave);
      if (!existente) {
        porCliente.set(chave, a);
      } else {
        const pNovo = prioridade[a.status] || 0;
        const pExist = prioridade[existente.status] || 0;
        if (pNovo > pExist || (pNovo === pExist && new Date(a.iniciado_em) > new Date(existente.iniciado_em))) {
          porCliente.set(chave, a);
        }
      }
    }
    return Array.from(porCliente.values());
  };

  const carregarFila = useCallback(async () => {
    if (!canalAtivo) return;
    try {
      const { data } = await api.get(`/canais/${canalAtivo.id}/fila`);
      setFila(prev => {
        const novosAguardando = data.filter(d => 
          d.status === 'aguardando' && !prev.find(p => p.id === d.id)
        );
        if (novosAguardando.length > 0) tocarSom('fila');
        // Ordenar: aguardando primeiro, em_atendimento, encerrados do mais recente
        return [...data].sort((a, b) => {
          const p = { aguardando: 0, em_atendimento: 1, encerrado: 2 };
          const pA = p[a.status] ?? 3;
          const pB = p[b.status] ?? 3;
          if (pA !== pB) return pA - pB;
          return new Date(b.encerrado_em || b.atualizado_em) - new Date(a.encerrado_em || a.atualizado_em);
        });
      });
    } catch {}
    // Carregar aviso do canal
    try {
      const { data: avisoData } = await api.get(`/aviso?canal_id=${canalAtivo.id}`);
      setAviso(avisoData);
    } catch {}
    // Carregar respostas rápidas pessoais do atendente
    try {
      const { data: respostasData } = await api.get('/respostas-rapidas');
      setRespostasRapidas(respostasData || []);
    } catch {}
  }, [canalAtivo]);

  // Polling a cada 5 segundos
  useEffect(() => {
    if (!canalAtivo) return;
    const interval = setInterval(() => { carregarFila(); }, 10000);
    return () => clearInterval(interval);
  }, [carregarFila]);

  // Polling de mensagens e status a cada 1.5 segundos quando há atendimento ativo
  useEffect(() => {
    if (!atendimentoAtivo) return;
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get(`/atendimentos/${atendimentoAtivo.id}/mensagens`);
        setMensagens(prev => {
          if (data.length > prev.length) {
            setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
            const novas = data.slice(prev.length);
            novas.forEach(m => {
              if (m.remetente === 'cliente') tocarSom('mensagem');
            });
          }
          // Mesclar com estado local para preservar campos atualizados em tempo real
          return data.map(d => {
            const local = prev.find(p => p.id === d.id);
            if (!local) return d;
            return {
              ...d,
              reacoes: local.reacoes ?? d.reacoes,
              atendente_nome: d.atendente_nome || local.atendente_nome,
              remetente_id: d.remetente_id || local.remetente_id,
            };
          });
        });
        // Verificar se atendimento atual foi encerrado
        const atAtual = data.find(m => m.atendimento_ref_id === atendimentoAtivo.id);
        if (atAtual && atAtual.atendimento_status === 'encerrado' && atendimentoAtivo.status !== 'encerrado') {
          setAtendimentoAtivoSync(null);
          setMensagens([]);
          setFila(f => f.map(a => a.id === atendimentoAtivo.id ? { ...a, status: 'encerrado' } : a));
          toast('✅ Atendimento encerrado pelo cliente');
        }
      } catch {}
    }, 6000);
    return () => clearInterval(interval);
  }, [atendimentoAtivo?.id]);

  useEffect(() => { carregarFila(); }, [carregarFila]);


  // Cache de nomes de atendentes para não perder em re-renders
  useEffect(() => {
    mensagens.forEach(msg => {
      if (msg.remetente === 'atendente' && msg.atendente_nome) {
        atendenteNomesRef.current[msg.id] = msg.atendente_nome;
      }
    });
  }, [mensagens]);

  // ESC para desselecionar atendimento
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (menuContextoRef.current) {
          menuContextoRef.current = null;
          setMenuContexto(null);
          return;
        }
      }
      if (e.key === 'Escape' && atendimentoAtivo) {
        setAtendimentoAtivoSync(null);
        setMensagens([]);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [atendimentoAtivo]);

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const onFilaNova = ({ atendimento }) => {
      // Adiciona à fila independente do canal ativo (a fila é filtrada por canal na renderização)
      setFila(f => f.find(a => a.id === atendimento.id) ? f : [...f, atendimento]);
      if (atendimento.canal_id !== canalAtivo?.id) return; // Notificações só do canal ativo
      tocarSom('fila');
      toast('🔔 Novo cliente na fila!', { duration: 5000 });
      chamarAtencao('Novo atendimento na fila');
      // Notificação do browser
      if (Notification.permission === 'granted') {
        const notif = new Notification(`${atendimento.cliente_nome || 'Novo cliente'}`, {
          body: 'Novo atendimento aguardando na fila',
          icon: '/icon-192.png',
          badge: '/icon-192.png',
          tag: `fila-${atendimento.id}`,
        });
        notif.onclick = () => {
          window.focus();
          notif.close();
        };
      }
    };

    const onMensagemNova = ({ mensagem, atendimento_id }) => {
      if (atendimentoAtivo?.id === atendimento_id) {
        setMensagens(m => [...m, mensagem]);
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
      } else if (mensagem.remetente === 'cliente') {
        // Incrementar badge apenas se o atendimento pertence ao usuário atual
        setFila(filaAtual => {
          const at = filaAtual.find(a => a.id === atendimento_id);
          if (at && at.atendente_id === usuario?.id) {
            setNaoLidas(prev => ({ ...prev, [atendimento_id]: (prev[atendimento_id] || 0) + 1 }));
          }
          return filaAtual;
        });
      }
      if (mensagem.remetente === 'cliente') {
        // Som e toast apenas se for atendimento do usuário atual
        const atFila = fila.find(a => a.id === atendimento_id);
        const ehMeuAtendimento = atFila?.atendente_id === usuario?.id || atendimentoAtivoRef.current?.id === atendimento_id;
        if (ehMeuAtendimento) {
          tocarSom('mensagem');
          chamarAtencao(`Mensagem de ${atFila?.cliente_nome || 'cliente'}`);
          if (atendimentoAtivoRef.current?.id === atendimento_id) {
            toast(`💬 ${mensagem.conteudo?.slice(0, 40) || 'Mídia recebida'}`, { duration: 3000 });
          }
        }
      }
    };

    const onAtendimentoStatus = ({ atendimento_id, status }) => {
      setFila(f => f.map(a => a.id === atendimento_id ? { ...a, status } : a));
      if (status === 'encerrado' && atendimentoAtivo?.id === atendimento_id) {
        setAtendimentoAtivoSync(prev => prev ? { ...prev, status: 'encerrado' } : null);
        toast('✅ Atendimento encerrado');
      }
    };

    const onAtendimentoTransferido = ({ atendimento_id, para_atendente_id, atendimento }) => {
      // Atendente que transferiu — remove da sua fila
      if (para_atendente_id !== usuario.id && atendimentoAtivo?.id === atendimento_id) {
        setAtendimentoAtivoSync(null);
        setMensagens([]);
        setFila(f => f.filter(a => a.id !== atendimento_id));
        toast('↗️ Atendimento transferido');
      }
      // Atendente que recebeu — adiciona na fila em tempo real
      if (para_atendente_id === usuario.id && atendimento) {
        setFila(f => {
          const jaExiste = f.find(a => a.id === atendimento_id);
          if (jaExiste) return f.map(a => a.id === atendimento_id ? { ...a, ...atendimento } : a);
          return [atendimento, ...f];
        });
        toast('📥 Você recebeu um atendimento transferido!', { duration: 5000 });
        if (Notification.permission === 'granted') {
          const notif = new Notification(`${atendimento.cliente_nome || 'Cliente'}`, {
            body: 'Atendimento transferido para você',
            icon: '/icon-192.png',
            badge: '/icon-192.png',
            tag: `transferido-${atendimento.id}`,
          });
          notif.onclick = () => {
            window.focus();
            notif.close();
          };
        }
      }
    };

    // Quando qualquer atendente assume um atendimento — atualiza fila de todos
    const onAtendimentoAssumido = ({ atendimento_id, atendente }) => {
      setFila(f => f.map(a => a.id === atendimento_id
        ? { ...a, status: 'em_atendimento', atendente_id: atendente.id, atendente_nome: atendente.nome }
        : a
      ));
      // Se este atendente estava vendo esse atendimento, atualiza também
      setAtendimentoAtivoSync(prev => prev?.id === atendimento_id
        ? { ...prev, status: 'em_atendimento', atendente_id: atendente.id }
        : prev
      );
    };

    // Sincroniza fila ao reconectar (evita perder atendimentos durante desconexão)
    const onFilaSync = (atendimentos) => {
      setFila(f => {
        const mapa = new Map(f.map(a => [a.id, a]));
        atendimentos.forEach(a => { if (!mapa.has(a.id)) mapa.set(a.id, a); });
        return Array.from(mapa.values()).sort((a, b) => (a.posicao_fila || 0) - (b.posicao_fila || 0));
      });
    };
    const onAtendimentosSync = (atendimentos) => {
      setFila(f => {
        const mapa = new Map(f.map(a => [a.id, a]));
        atendimentos.forEach(a => { mapa.set(a.id, a); });
        return Array.from(mapa.values());
      });
    };
    socket.on('fila:sync', onFilaSync);
    socket.on('atendimentos:sync', onAtendimentosSync);
    socket.on('fila:nova', onFilaNova);
    let reconnectTimer = null;
    socket.on('connect', () => {
      clearTimeout(reconnectTimer);
      reconnectTimer = setTimeout(() => { carregarFila(); }, 2000);
    });
    let presenceTimeout = null;
    socket.on('cliente:presence', ({ jid, presence }) => {
      const at = atendimentoAtivoRef.current;
      if (!at) return;
      const jidMatch = jid === at.jid_resposta
        || jid === `${at.numero_whatsapp}@s.whatsapp.net`
        || (at.numero_whatsapp && jid.includes(at.numero_whatsapp.replace('lid_','')));
      if (!jidMatch) return;
      if (presenceTimeout) clearTimeout(presenceTimeout);
      if (presence === 'composing') {
        setPresenceCliente('digitando...');
        // Auto-limpar após 8 segundos caso não chegue evento paused
        presenceTimeout = setTimeout(() => setPresenceCliente(null), 3000);
      } else if (presence === 'recording') {
        setPresenceCliente('gravando áudio...');
        presenceTimeout = setTimeout(() => setPresenceCliente(null), 3000);
      } else {
        setPresenceCliente(null);
      }
    });
    const onAvisoAtualizado = ({ aviso_ativo, aviso_texto }) => {
      setAviso({ aviso_ativo, aviso_texto });
    };
    socket.on('aviso:atualizado', onAvisoAtualizado);
    const onMensagemDeletada = ({ mensagem_id }) => {
      setMensagens(m => m.map(msg => msg.id === mensagem_id
        ? { ...msg, conteudo: '🚫 Mensagem apagada', tipo: 'deletada' }
        : msg
      ));
    };
    const onMensagemEditada = ({ mensagem_id, novo_conteudo }) => {
      setMensagens(m => m.map(msg => msg.id === mensagem_id
        ? { ...msg, conteudo: novo_conteudo, editado: true }
        : msg
      ));
    };
    const onMensagemReacao = ({ mensagem_id, atendimento_id, remetente, emoji }) => {
      setMensagens(m => m.map(msg => {
        if (msg.id !== mensagem_id) return msg;
        const reacoes = (msg.reacoes || []).filter(r => r.remetente !== remetente);
        if (emoji) reacoes.push({ remetente, emoji });
        return { ...msg, reacoes };
      }));
      // Badge de não lidas para reações do cliente em atendimento não ativo
      if (remetente === 'cliente' && emoji && atendimento_id !== atendimentoAtivoRef.current?.id) {
        setFila(filaAtual => {
          const at = filaAtual.find(a => a.id === atendimento_id);
          if (at && at.atendente_id === usuario?.id) {
            setNaoLidas(prev => ({ ...prev, [atendimento_id]: (prev[atendimento_id] || 0) + 1 }));
          }
          return filaAtual;
        });
      }
    };
    socket.on('mensagem:deletada', onMensagemDeletada);
    socket.on('mensagem:editada', onMensagemEditada);
    socket.on('mensagem:reacao', onMensagemReacao);
    socket.on('mensagem:nova', onMensagemNova);
    socket.on('atendimento:status', onAtendimentoStatus);
    socket.on('atendimento:transferido', onAtendimentoTransferido);
    socket.on('atendimento:assumido', onAtendimentoAssumido);

    return () => {
      socket.off('fila:sync', onFilaSync);
      socket.off('atendimentos:sync', onAtendimentosSync);
      socket.off('fila:nova', onFilaNova);
      socket.off('aviso:atualizado', onAvisoAtualizado);
      socket.off('mensagem:deletada', onMensagemDeletada);
      socket.off('mensagem:editada', onMensagemEditada);
      socket.off('mensagem:reacao', onMensagemReacao);
      socket.off('mensagem:nova', onMensagemNova);
      socket.off('atendimento:status', onAtendimentoStatus);
      socket.off('atendimento:transferido', onAtendimentoTransferido);
      socket.off('atendimento:assumido', onAtendimentoAssumido);
    };
  }, [canalAtivo, atendimentoAtivo, usuario, carregarFila]);



  // Pedir permissão de notificação
  useEffect(() => {
    if (Notification.permission === 'default') Notification.requestPermission();
  }, []);

  const assumirAtendimento = async (at) => {
    console.log("ASSUMINDO:", at?.id);
    setNaoLidas(prev => { const n = {...prev}; delete n[at.id]; return n; });
    try {
      await api.post(`/atendimentos/${at.id}/assumir`);
      setFila(f => f.map(a => a.id === at.id ? { ...a, status: 'em_atendimento', atendente_id: usuario.id } : a));
      setAtendimentoAtivoSync({ ...at, status: 'em_atendimento', atendente_id: usuario.id });
      const { data } = await api.get(`/atendimentos/${at.id}/mensagens`);
      setMensagens(data);
      setTimeout(() => messagesEndRef.current?.scrollIntoView(), 100);
      setTimeout(() => document.querySelector('.chat-textarea')?.focus(), 150);
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao assumir atendimento');
    }
  };

  const selecionarAtendimento = async (at) => {
    setNaoLidas(prev => { const n = {...prev}; delete n[at.id]; return n; });
    if (at.status === 'aguardando') {
      await assumirAtendimento(at);
      return;
    }
    if (at.atendente_id && at.atendente_id !== usuario.id && at.status === 'em_atendimento') return;
    setAtendimentoAtivoSync(at);
    const { data } = await api.get(`/atendimentos/${at.id}/mensagens`);
    setMensagens(data);
    setPresenceCliente(null);
    api.post(`/atendimentos/${at.id}/presenca`).catch(() => {});
    setTimeout(() => messagesEndRef.current?.scrollIntoView(), 100);
    setTimeout(() => document.querySelector('.chat-textarea')?.focus(), 150);
  };

  const encerrarAtendimento = async () => {
    if (!atendimentoAtivo || !confirm('Encerrar este atendimento?')) return;
    try {
      await api.post(`/atendimentos/${atendimentoAtivo.id}/encerrar`);
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao encerrar');
    }
  };

  const reabrirAtendimento = async () => {
    if (!atendimentoAtivo) return;
    try {
      const { data: novoAt } = await api.post(`/atendimentos/${atendimentoAtivo.id}/reabrir`);
      const atAtualizado = { ...atendimentoAtivo, id: novoAt.id, status: 'em_atendimento', atendente_id: novoAt.atendente_id };
      setAtendimentoAtivoSync(atAtualizado);
      setFila(f => [atAtualizado, ...f.filter(a => a.id !== atendimentoAtivo.id)]);
      const { data: msgs } = await api.get(`/atendimentos/${novoAt.id}/mensagens`);
      setMensagens(msgs);
      toast.success('Atendimento reaberto!');
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao reabrir');
    }
  };

  const salvarNomeCliente = async () => {
    if (!novoNome.trim() || !atendimentoAtivo) return;
    try {
      await api.put(`/clientes/${atendimentoAtivo.cliente_id || atendimentoAtivo.id}`, { nome: novoNome });
      setAtendimentoAtivoSync(prev => ({ ...prev, cliente_nome: novoNome }));
      setFila(f => f.map(a => a.id === atendimentoAtivo.id ? { ...a, cliente_nome: novoNome } : a));
      setEditandoNome(false);
    } catch { setEditandoNome(false); }
  };

  const salvarNumeroCliente = async () => {
    if (!novoNumero.trim() || !atendimentoAtivo) return;
    try {
      const { data } = await api.put(`/clientes/${atendimentoAtivo.cliente_id || atendimentoAtivo.id}`, { numero_whatsapp: novoNumero });
      const n = data.numero_whatsapp || novoNumero.replace(/\D/g, '');
      setAtendimentoAtivoSync(prev => ({ ...prev, numero_whatsapp: n }));
      setFila(f => f.map(a => a.id === atendimentoAtivo.id ? { ...a, numero_whatsapp: n } : a));
      setEditandoNumero(false);
    } catch { setEditandoNumero(false); }
  };

  const enviarMensagem = async () => {
    if (!texto.trim() || !atendimentoAtivo || enviando || atendimentoAtivo.status === 'encerrado') return;
    setEnviando(true);
    const t = texto;
    const r = respondendo;
    setTexto('');
    setRespondendo(null);
    try {
      const payload = { texto: t };
      if (r) {
        payload.mensagem_citada_id = r.id;
        payload.mensagem_citada_conteudo = r.conteudo || '[mídia]';
        payload.mensagem_citada_remetente = r.remetente;
      }
      await api.post(`/atendimentos/${atendimentoAtivo.id}/mensagem`, payload);
    } catch {
      toast.error('Erro ao enviar mensagem');
      setTexto(t);
    } finally { setEnviando(false); }
  };

  const enviarArquivoDirecto = async (arquivo) => {
    const form = new FormData();
    form.append('arquivo', arquivo);
    const tipo = arquivo.type.startsWith('image/') ? 'imagem' : arquivo.type.startsWith('audio/') ? 'audio' : arquivo.type.startsWith('video/') ? 'video' : 'documento';
    form.append('tipo', tipo);
    try {
      const { data } = await api.post(`/atendimentos/${atendimentoAtivo.id}/midia`, form);
      setMensagens(m => [...m, { ...data, remetente: 'atendente', atendente_nome: usuario.nome, enviado_em: new Date() }]);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
      toast.success('Arquivo enviado');
    } catch { toast.error('Erro ao enviar arquivo'); }
  };
  const enviarArquivo = async (e) => {
    const arquivo = e.target.files[0];
    if (!arquivo || !atendimentoAtivo || atendimentoAtivo.status === 'encerrado') return;
    e.target.value = '';
    // Se for imagem, abrir editor
    if (arquivo.type.startsWith('image/')) {
      const url = URL.createObjectURL(arquivo);
      editorShapesRef.current = [];
      editorImgRef.current = null;
      setEditorFerramenta('retangulo');
      setEditorCor('#e53935');
      setEditorTextoPos(null);
      setEditorTextoInput('');
      setImagemParaEditar({ arquivo, url });
      return;
    }
    await enviarArquivoDirecto(arquivo);
  };

  const iniciarGravacao = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Usar formato suportado pelo navegador
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : 'audio/ogg;codecs=opus';
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      mediaRecorder.ondataavailable = e => audioChunksRef.current.push(e.data);
      mediaRecorder.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        const ext = mimeType.includes('webm') ? 'webm' : 'ogg';
        const file = new File([blob], `audio.${ext}`, { type: mimeType });
        stream.getTracks().forEach(t => t.stop());
        const url = URL.createObjectURL(blob);
        setAudioPendente({ blob, url, file });
      };
      mediaRecorder.start();
      setGravando(true);
    } catch { toast.error('Permissão de microfone negada'); }
  };

  const pararGravacao = () => {
    mediaRecorderRef.current?.stop();
    setGravando(false);
  };
  const enviarAudioPendente = async () => {
    if (!audioPendente) return;
    const form = new FormData();
    form.append('arquivo', audioPendente.file);
    form.append('tipo', 'audio');
    try {
      const { data } = await api.post(`/atendimentos/${atendimentoAtivo.id}/midia`, form);
      setMensagens(m => [...m, { ...data, remetente: 'atendente', atendente_nome: usuario.nome, tipo: 'audio', enviado_em: new Date() }]);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
      toast.success('Áudio enviado');
    } catch { toast.error('Erro ao enviar áudio'); }
    URL.revokeObjectURL(audioPendente.url);
    setAudioPendente(null);
  };
  const cancelarAudioPendente = () => {
    if (audioPendente) URL.revokeObjectURL(audioPendente.url);
    setAudioPendente(null);
  };

  const abrirChamada = () => {
    const numero = atendimentoAtivo?.numero_whatsapp;
    if (!numero) return toast.error('Número não disponível');
    const n = numero.replace(/\D/g, '');
    window.open(`https://wa.me/${n}`, '_blank');
  };

  const abrirTransferir = async () => {
    try {
      const canalId = atendimentoAtivo?.canal_id || canalAtivo?.id;
      const { data } = await api.get(`/canais/${canalId}/atendentes`);
      setAtendentes(data);
      setTransferirModal(true);
    } catch (err) {
      toast.error('Erro ao carregar atendentes');
    }
  };

  const fazerTransferencia = async (paraId, observacao = '') => {
    try {
      await api.post(`/atendimentos/${atendimentoAtivo.id}/transferir`, { para_atendente_id: paraId, observacao });
      setTransferirModal(false);
      // Remove da fila do atendente atual
      setFila(f => f.filter(a => a.id !== atendimentoAtivo.id));
      setAtendimentoAtivoSync(null);
      setMensagens([]);
      toast.success('Transferido com sucesso');
    } catch (err) { toast.error(err.response?.data?.erro || 'Erro ao transferir'); }
  };

  const excluirMensagem = async (msg) => {
    if (!window.confirm('Apagar esta mensagem?')) return;
    try {
      await api.delete(`/mensagens/${msg.id}`);
      setMensagens(m => m.map(x => x.id === msg.id ? { ...x, conteudo: '🚫 Mensagem apagada', tipo: 'deletada' } : x));
    } catch(err) { toast.error(err.response?.data?.erro || 'Erro ao apagar'); }
  };

  const editarMensagem = async () => {
    if (!textoEdicao.trim() || !editandoMsg) return;
    try {
      const msgId = editandoMsg.id;
      const novoConteudo = textoEdicao;
      const nomeUsuario = usuario?.nome;
      setEditandoMsg(null);
      setTextoEdicao('');
      await api.put(`/mensagens/${msgId}`, { texto: novoConteudo });
      setMensagens(prev => prev.map(x => x.id === msgId
        ? { ...x, conteudo: novoConteudo, remetente_id: x.remetente_id || editandoMsgRef.current?.remetente_id, atendente_nome: x.atendente_nome || nomeUsuario }
        : x
      ));
      toast.success('Mensagem editada!');
    } catch(err) { toast.error(err.response?.data?.erro || 'Erro ao editar'); }
  };

  const tempoAtras = (date) => formatDistanceToNow(new Date(date), { addSuffix: true, locale: ptBR });
  const atEncerrado = atendimentoAtivo?.status === 'encerrado';

  const renderFilaItem = (at, destaque = false) => (
    <div key={at.id}
      className={`fila-item ${destaque ? 'destaque' : ''} ${atendimentoAtivo?.id === at.id ? 'selecionado' : ''} ${at.status === 'encerrado' ? 'encerrado' : ''} ${at.atendente_id && at.atendente_id !== usuario.id && at.status === 'em_atendimento' ? 'outro-atendente' : ''}`}
      onClick={() => selecionarAtendimento(at)}>
      <div className="fila-avatar" style={{position:'relative'}}>
        {(at.cliente_nome || 'CL').slice(0, 2).toUpperCase()}
        {naoLidas[at.id] > 0 && (
          <span style={{position:'absolute',top:-6,right:-6,background:'#e53935',color:'white',borderRadius:'50%',fontSize:10,fontWeight:700,minWidth:18,height:18,display:'flex',alignItems:'center',justifyContent:'center',padding:'0 4px',boxShadow:'0 1px 4px rgba(0,0,0,0.4)'}}>{naoLidas[at.id] > 99 ? '99+' : naoLidas[at.id]}</span>
        )}
      </div>
      <div className="fila-info">
        <span className="fila-nome">{at.cliente_nome || at.numero_whatsapp}</span>
        {at.tipo_atendimento && at.tipo_atendimento !== 'aguardando_menu' && <span className="fila-preview">{at.tipo_atendimento}</span>}
        <div className="fila-meta">
          <span className="fila-tempo">{tempoAtras(at.iniciado_em)}</span>
          <span className={`fila-status ${at.status}`}>
            {{ aguardando: 'Aguardando', em_atendimento: 'Em atendimento', encerrado: 'Encerrado' }[at.status]}
          </span>
        </div>
      </div>
    </div>
  );

  // Separadores de histórico
  const mensagensComSep = [];
  let ultimoAtId = null;
  for (const msg of mensagens) {
    if (msg.atendimento_ref_id && msg.atendimento_ref_id !== ultimoAtId) {
      if (ultimoAtId !== null) mensagensComSep.push({ _separador: true, id: `sep_${msg.atendimento_ref_id}` });
      ultimoAtId = msg.atendimento_ref_id;
    }
    mensagensComSep.push(msg);
  }

  return (
    <div className="atendimento-layout">
      {telaFlash && <div className="tela-flash-alerta" />}
      {/* PAINEL ESQUERDO */}
      <div className={`painel-fila${painelRecolhido ? ' recolhido' : ''}`}>

        {/* AVISO TEMPORÁRIO */}
        <div style={{padding:'8px 12px',borderBottom:'1px solid var(--border)'}}>
          {aviso.aviso_ativo && (
            <div style={{background:'#7c2d12',color:'#fde68a',borderRadius:6,padding:'6px 10px',fontSize:12,marginBottom:6,display:'flex',alignItems:'flex-start',gap:6}}>
              <span>⚠️</span>
              <span style={{flex:1,lineHeight:1.4}}>{aviso.aviso_texto}</span>
            </div>
          )}
          <button onClick={() => { setAvisoTextoEdit(aviso.aviso_texto || ''); setShowAvisoEditor(s => !s); }}
            style={{width:'100%',padding:'5px 8px',borderRadius:6,border:'1px solid var(--border)',background:aviso.aviso_ativo?'#7c2d12':'var(--bg-elevated)',color:aviso.aviso_ativo?'#fde68a':'var(--text-muted)',fontSize:12,cursor:'pointer',textAlign:'left'}}>
            {aviso.aviso_ativo ? '⚠️ Aviso ativo — clique para editar' : '+ Ativar aviso temporário'}
          </button>
          {showAvisoEditor && (
            <div style={{marginTop:6,display:'flex',flexDirection:'column',gap:6}}>
              <textarea value={avisoTextoEdit} onChange={e => setAvisoTextoEdit(e.target.value)}
                placeholder="Digite o aviso que será enviado automaticamente..."
                style={{width:'100%',padding:8,borderRadius:6,border:'1px solid var(--border)',background:'var(--bg-surface)',color:'var(--text-primary)',fontSize:12,resize:'vertical',minHeight:70,boxSizing:'border-box'}} />
              <div style={{display:'flex',gap:6}}>
                <button onClick={async () => {
                  try {
                    await api.put('/aviso', { canal_id: canalAtivo.id, aviso_ativo: true, aviso_texto: avisoTextoEdit });
                    setShowAvisoEditor(false);
                    toast.success('Aviso ativado!');
                  } catch { toast.error('Erro ao salvar aviso'); }
                }} style={{flex:1,padding:'5px 8px',borderRadius:6,border:'none',background:'#b45309',color:'white',fontSize:12,cursor:'pointer',fontWeight:600}}>
                  ✓ Ativar
                </button>
                <button onClick={async () => {
                  try {
                    await api.put('/aviso', { canal_id: canalAtivo.id, aviso_ativo: false, aviso_texto: '' });
                    setShowAvisoEditor(false);
                    toast.success('Aviso desativado!');
                  } catch { toast.error('Erro ao desativar aviso'); }
                }} style={{flex:1,padding:'5px 8px',borderRadius:6,border:'1px solid var(--border)',background:'var(--bg-elevated)',color:'var(--text-muted)',fontSize:12,cursor:'pointer'}}>
                  ✕ Desativar
                </button>
              </div>
            </div>
          )}
        </div>
        {/* CANAL */}
        <div className="canal-selector">
          {canais.map(c => (
            <div key={c.id}
              className={`canal-chip ${canalAtivo?.id === c.id ? 'ativo' : ''}`}
              onClick={() => { setCanalAtivo(c); setAtendimentoAtivoSync(null); setMensagens([]); }}>
              <span className={`canal-dot ${c.status_conexao}`} />
              {c.nome}
            </div>
          ))}
        </div>

        {/* BOTÃO NOVA CONVERSA */}
        <div style={{padding:'8px 12px',borderBottom:'1px solid var(--border)'}}>
          <button onClick={() => setModalNovaConversa(true)}
            style={{width:'100%',background:'var(--orange)',border:'none',borderRadius:'var(--radius)',padding:'8px',color:'white',fontSize:13,fontWeight:600,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:6}}>
            <span style={{fontSize:16}}>+</span> Nova Conversa
          </button>
        </div>
        <div style={{padding:'8px 12px',borderBottom:'1px solid var(--border)'}}>
          <input
            type="text"
            placeholder="🔍 Pesquisar por nome..."
            value={buscaFila}
            onChange={e => setBuscaFila(e.target.value)}
            style={{width:'100%',background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:'7px 10px',fontSize:12,color:'var(--text-primary)',outline:'none'}}
          />
        </div>
        <div className="fila-lista">
        {/* ÁREA DE FILA — AGUARDANDO */}
        {aguardando.length > 0 && (
          <div className="fila-secao aguardando-secao">
            <div className="fila-secao-header aguardando-header">
              <span>🔔 Aguardando atendimento</span>
              <span className="badge-fila">{aguardando.length}</span>
            </div>
            <div className="fila-secao-desc">Clique para iniciar o atendimento</div>
            {aguardando.map(at => renderFilaItem(at, true))}
          </div>
        )}

        {/* EM ATENDIMENTO */}
        {emAtendimento.length > 0 && (
          <div className="fila-secao">
            <div className="fila-secao-header">
              <span>🎧 Em atendimento</span>
              <span className="badge-fila verde">{emAtendimento.length}</span>
            </div>
            {emAtendimento.map(at => renderFilaItem(at))}
          </div>
        )}

        {/* ENCERRADOS */}
        {encerrados.length > 0 && (
          <div className="fila-secao">
            <div className="fila-secao-header cinza">
              <span>✓ Encerrados</span>
              <span className="badge-fila cinza">{encerrados.length}</span>
            </div>
            {encerrados.map(at => renderFilaItem(at))}
          </div>
        )}

        {fila.length === 0 && (
          <div className="fila-vazia">Nenhum atendimento</div>
        )}
        </div>
      </div>

      <button type="button" className="btn-toggle-painel"
        onClick={() => setPainelRecolhido(v => !v)}
        title={painelRecolhido ? 'Mostrar lista de conversas' : 'Recolher lista de conversas'}>
        {painelRecolhido ? '›' : '‹'}
      </button>

      {/* CHAT */}
      <div className="chat-area">
        {!atendimentoAtivo ? (
          <div className="chat-vazio">
            <img src="/uploads/atendimento-icon.png" alt="Atendimento"
              style={{width:180, maxHeight:180, objectFit:'contain', opacity:0.5, marginBottom:8}}
              onError={e => e.target.style.display='none'} />
            <p>Selecione um atendimento</p>
            {aguardando.length > 0 && (
              <button className="btn-proximo" onClick={() => assumirAtendimento(aguardando[0])}>
                Atender próximo da fila →
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="chat-header">
              <div className="chat-avatar-grande">{(atendimentoAtivo.cliente_nome || 'CL').slice(0, 2).toUpperCase()}</div>
              <div className="chat-header-info">
                {editandoNome ? (
                  <div className="editar-nome-wrap">
                    <input className="editar-nome-input" value={novoNome} onChange={e => setNovoNome(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') salvarNomeCliente(); if (e.key === 'Escape') setEditandoNome(false); }} autoFocus />
                    <button className="btn-salvar-nome" onClick={salvarNomeCliente}>✓</button>
                    <button className="btn-cancelar-nome" onClick={() => setEditandoNome(false)}>✕</button>
                  </div>
                ) : (
                  <div className="nome-cliente-wrap">
                    <strong>{atendimentoAtivo.cliente_nome || atendimentoAtivo.numero_whatsapp}</strong>
                    {presenceCliente && <span style={{fontSize:11, color:'var(--orange-light)', fontWeight:400, marginLeft:6}}>🟢 {presenceCliente}</span>}
                    <button className="btn-editar-nome" onClick={() => { setNovoNome(atendimentoAtivo.cliente_nome || ''); setEditandoNome(true); }}>✏️</button>
                  </div>
                )}
                <span>
                  {editandoNumero ? (
                    <span className="editar-nome-wrap" style={{display:'inline-flex', alignItems:'center', gap:4}}>
                      <input className="editar-nome-input" style={{width:140, fontSize:12}} value={novoNumero}
                        onChange={e => setNovoNumero(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') salvarNumeroCliente(); if (e.key === 'Escape') setEditandoNumero(false); }}
                        placeholder="55479999-9999" autoFocus />
                      <button className="btn-salvar-nome" onClick={salvarNumeroCliente}>✓</button>
                      <button className="btn-cancelar-nome" onClick={() => setEditandoNumero(false)}>✕</button>
                    </span>
                  ) : (
                    <span>
                      {formatarNumero(atendimentoAtivo.numero_whatsapp) && `${formatarNumero(atendimentoAtivo.numero_whatsapp)} `}
                      <button className="btn-editar-nome" onClick={() => { setNovoNumero(''); setEditandoNumero(true); }}>✏️</button>
                      {' · '}{canalAtivo?.nome}
                    </span>
                  )}
                  {atEncerrado && <span className="badge-encerrado"> · Encerrado</span>}
                </span>
              </div>
              {!atEncerrado && (
                <div className="chat-header-acoes">
                  <button className="btn-acao transferir" onClick={abrirTransferir}>↗ Transferir</button>
                  <button className="btn-acao encerrar" onClick={encerrarAtendimento}>✕ Encerrar</button>
                </div>
              )}
            </div>

            <div className="mensagens-lista">
              {mensagensComSep.filter(msg => msg._separador || msg.conteudo || msg.tipo === 'audio' || msg.tipo === 'imagem' || msg.tipo === 'video' || msg.tipo === 'documento' || msg.tipo === 'sticker').map(msg => {
                if (msg._separador) return (
                  <div key={msg.id} className="separador-atendimento">
                    <span>— Atendimento anterior —</span>
                  </div>
                );
                return (
                  <div key={msg.id} className={`msg ${msg.remetente === 'cliente' ? 'recebida' : msg.remetente === 'sistema' ? 'sistema' : 'enviada'}`}
                    onContextMenu={e => { e.preventDefault(); const m = { msg, x: e.clientX, y: e.clientY }; menuContextoRef.current = m; setMenuContexto(m); }}>
                    {msg.remetente === 'atendente' && (
                      <span className="msg-atendente-nome">
                        {msg.atendente_nome || atendenteNomesRef.current[msg.id] || usuario?.nome || 'Atendente'}
                      </span>
                    )}
                    {msg.tipo === 'texto' && (
                      <div className="msg-bubble">
                        {msg.mensagem_citada_conteudo && (
                          <div style={{
                            background: msg.remetente === 'cliente' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.2)',
                            borderLeft: '3px solid rgba(255,255,255,0.6)',
                            borderRadius: '4px',
                            padding: '4px 8px',
                            marginBottom: 6,
                            fontSize: 12,
                            opacity: 0.9,
                          }}>
                            <div style={{fontWeight:600, fontSize:11, marginBottom:2}}>
                              {msg.mensagem_citada_remetente === 'atendente' ? '👤 Atendente' : '👥 Cliente'}
                            </div>
                            <div style={{whiteSpace:'pre-wrap'}}>{msg.mensagem_citada_conteudo.slice(0,100)}{msg.mensagem_citada_conteudo.length > 100 ? '...' : ''}</div>
                          </div>
                        )}
                        {msg.conteudo?.split("\n").map((l, i) => <span key={i}>{l}<br/></span>)}
                        {msg.editado && <span style={{fontSize:11,opacity:0.6,fontStyle:'italic'}}>editado</span>}
                      </div>
                    )}
                    {msg.tipo === 'audio' && (() => {
                      const src = msg.arquivo_nome ? (msg.arquivo_nome.startsWith('/') ? msg.arquivo_nome : `/uploads/${msg.arquivo_nome}`) : null;
                      return <div className="msg-bubble" style={{padding:'6px 10px'}}>
                        {src ? <audio controls src={src} style={{maxWidth:220,height:36}} /> : '🎵 Áudio'}
                      </div>;
                    })()}
                    {msg.tipo === 'imagem' && (() => {
                      const src = msg.arquivo_nome ? (msg.arquivo_nome.startsWith('/') ? msg.arquivo_nome : `/uploads/${msg.arquivo_nome}`) : null;
                      return <div className="msg-bubble" style={{padding:4}}>
                        {src ? <img src={src} alt="Imagem" style={{maxWidth:220,borderRadius:8,display:'block',cursor:'pointer'}} onClick={() => window.open(src,'_blank')} />
                          : <div style={{padding:'8px 4px',fontSize:13,color:'var(--text-muted)',maxWidth:220}}>⚠️ Não foi possível carregar esta imagem.<br/>Peça para o cliente reenviar como foto (não como arquivo/documento).</div>}
                        {msg.conteudo && <span style={{display:'block',padding:'4px 6px 2px',fontSize:13,lineHeight:1.4}}>{msg.conteudo}</span>}
                      </div>;
                    })()}


                    {msg.tipo === 'video' && (() => {
                      const src = msg.arquivo_nome ? (msg.arquivo_nome.startsWith('/') ? msg.arquivo_nome : `/uploads/${msg.arquivo_nome}`) : null;
                      return <div className="msg-bubble" style={{padding:4}}>
                        {src ? <video controls src={src} style={{maxWidth:220,borderRadius:8}} /> : '🎬 Vídeo'}
                      </div>;
                    })()}
                    {msg.tipo === 'documento' && (() => {
                      const src = msg.arquivo_nome ? (msg.arquivo_nome.startsWith('/') ? msg.arquivo_nome : `/uploads/${msg.arquivo_nome}`) : null;
                      const nome = msg.arquivo_nome ? msg.arquivo_nome.split('/').pop() : 'Documento';
                      return <div className="msg-bubble" style={{display:'flex',flexDirection:'column',gap:4}}>
                        {src ? <a href={src} target="_blank" rel="noreferrer" style={{color:'inherit',display:'flex',alignItems:'center',gap:6}}>📄 {nome}</a> : '📄 Documento'}
                        {msg.conteudo && <span style={{fontSize:13,marginTop:2}}>{msg.conteudo}</span>}
                      </div>;
                    })()}
                    {msg.tipo === 'sticker' && (() => {
                      const src = msg.arquivo_nome ? (msg.arquivo_nome.startsWith('/') ? msg.arquivo_nome : `/uploads/${msg.arquivo_nome}`) : null;
                      return <div className="msg-bubble" style={{padding:4,background:'transparent',border:'none',boxShadow:'none'}}>
                        {src ? <img src={src} alt="Figurinha" style={{maxWidth:140,borderRadius:8,display:'block'}} /> : '🎨 Figurinha'}
                      </div>;
                    })()}
                    {msg.reacoes && msg.reacoes.length > 0 && (
                      <div style={{display:'flex', flexWrap:'wrap', gap:3, marginTop:3}}>
                        {msg.reacoes.map((r, i) => (
                          <span key={i} style={{
                            fontSize:14, background:'rgba(0,0,0,0.08)', borderRadius:10,
                            padding:'1px 5px', cursor:'default', display:'inline-flex', alignItems:'center', gap:2
                          }} title={r.remetente === 'cliente' ? 'Cliente' : 'Atendente'}>
                            {r.emoji}
                          </span>
                        ))}
                      </div>
                    )}
                    <span className="msg-hora">
                      {(() => { const d = new Date(msg.enviado_em); return isNaN(d) ? '' : d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' }); })()}
                      {msg.remetente === 'atendente' && ' ✓'}
                    </span>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {atEncerrado ? (
              <div className="chat-encerrado-banner">
                <span>🔒 Atendimento encerrado</span>
                <button className="btn-reabrir" onClick={reabrirAtendimento}>🔄 Reabrir atendimento</button>
              </div>
            ) : (
              <div className="chat-input-area">
                {audioPendente && (
                  <div style={{
                    display:'flex', alignItems:'center', gap:10,
                    background:'var(--bg-secondary, #f5f5f5)', borderLeft:'3px solid #25d366',
                    padding:'8px 12px', borderRadius:8, margin:'0 0 6px 0'
                  }}>
                    <audio controls src={audioPendente.url} style={{height:36, flex:1, minWidth:0}} />
                    <button
                      onClick={enviarAudioPendente}
                      style={{background:'#25d366', color:'white', border:'none', borderRadius:6, padding:'6px 14px', cursor:'pointer', fontWeight:600, fontSize:13}}
                      title="Enviar áudio"
                    >▶ Enviar</button>
                    <button
                      onClick={cancelarAudioPendente}
                      style={{background:'#e53935', color:'white', border:'none', borderRadius:6, padding:'6px 14px', cursor:'pointer', fontWeight:600, fontSize:13}}
                      title="Cancelar"
                    >✕ Cancelar</button>
                  </div>
                )}
                {editandoMsg && (
                  <div style={{
                    display:'flex', alignItems:'center', gap:8,
                    background:'#fff8e1', borderLeft:'3px solid #f9a825',
                    padding:'6px 12px', fontSize:12, color:'#555', marginBottom:4
                  }}>
                    <span style={{flex:1}}>✏️ Editando: <em>{editandoMsg.conteudo?.slice(0, 60)}{editandoMsg.conteudo?.length > 60 ? '...' : ''}</em></span>
                    <button onClick={() => { setEditandoMsg(null); setTextoEdicao(''); }}
                      style={{background:'none', border:'none', cursor:'pointer', fontSize:16, color:'#999'}}>✕</button>
                  </div>
                )}
                {respondendo && (
                  <div style={{
                    display:'flex', alignItems:'center', gap:8,
                    background:'var(--orange-bg)', borderLeft:'3px solid var(--orange)',
                    padding:'6px 12px', fontSize:12, color:'var(--text-muted)'
                  }}>
                    <span style={{flex:1}}>↩️ Respondendo: <strong>{respondendo.conteudo?.slice(0,60) || '[mídia]'}</strong></span>
                    <button onClick={() => setRespondendo(null)} style={{background:'none',border:'none',cursor:'pointer',fontSize:16,color:'var(--text-muted)'}}>✕</button>
                  </div>
                )}
                {showEmoji && (
                  <div className="emoji-wrapper">
                    <EmojiPicker onEmojiClick={({ emoji }) => { setTexto(t => t + emoji); setShowEmoji(false); setTimeout(() => document.querySelector('.chat-textarea')?.focus(), 50); }} height={350} />
                  </div>
                )}
                {showRespostasRapidas && (
                  <div style={{position:'absolute',bottom:'100%',left:0,marginBottom:6,background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:10,padding:6,zIndex:100,display:'flex',flexDirection:'column',gap:2,minWidth:260,maxHeight:320,overflowY:'auto',boxShadow:'0 4px 16px rgba(0,0,0,0.3)'}}>
                    {respostasRapidas.length === 0 && (
                      <div style={{padding:'8px 10px',fontSize:12,color:'var(--text-muted)'}}>Nenhuma resposta rápida ainda.</div>
                    )}
                    {respostasRapidas.map((r, i) => (
                      <div key={i} style={{display:'flex',alignItems:'center',gap:4,borderRadius:6}}
                        onMouseEnter={e => e.currentTarget.style.background='var(--bg-hover)'}
                        onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                        <button onClick={() => {
                          setTexto(r);
                          setShowRespostasRapidas(false);
                          setTimeout(() => document.querySelector('.chat-textarea')?.focus(), 50);
                        }} style={{flex:1,textAlign:'left',padding:'8px 4px 8px 10px',border:'none',background:'transparent',color:'var(--text-primary)',fontSize:13,cursor:'pointer'}}>
                          {r}
                        </button>
                        <button title="Remover" onClick={async () => {
                          const nova = respostasRapidas.filter((_, idx) => idx !== i);
                          setRespostasRapidas(nova);
                          try { await api.put('/respostas-rapidas', { respostas: nova }); }
                          catch { toast.error('Erro ao remover resposta rápida'); }
                        }} style={{background:'transparent',border:'none',color:'var(--text-muted)',fontSize:13,cursor:'pointer',padding:'6px 10px 6px 4px'}}>
                          ✕
                        </button>
                      </div>
                    ))}
                    <div style={{display:'flex',gap:4,borderTop:'1px solid var(--border)',marginTop:4,paddingTop:6}}>
                      <input value={novaResposta} onChange={e => setNovaResposta(e.target.value)}
                        placeholder="Nova resposta rápida..."
                        onKeyDown={async e => {
                          if (e.key === 'Enter' && novaResposta.trim()) {
                            const nova = [...respostasRapidas, novaResposta.trim()];
                            setRespostasRapidas(nova);
                            setNovaResposta('');
                            try { await api.put('/respostas-rapidas', { respostas: nova }); }
                            catch { toast.error('Erro ao salvar resposta rápida'); }
                          }
                        }}
                        style={{flex:1,padding:'6px 8px',borderRadius:6,border:'1px solid var(--border)',background:'var(--bg-surface)',color:'var(--text-primary)',fontSize:12}} />
                      <button onClick={async () => {
                        if (!novaResposta.trim()) return;
                        const nova = [...respostasRapidas, novaResposta.trim()];
                        setRespostasRapidas(nova);
                        setNovaResposta('');
                        try { await api.put('/respostas-rapidas', { respostas: nova }); }
                        catch { toast.error('Erro ao salvar resposta rápida'); }
                      }} style={{padding:'6px 10px',borderRadius:6,border:'none',background:'var(--tema-cor)',color:'white',fontSize:12,cursor:'pointer',fontWeight:600}}>
                        +
                      </button>
                    </div>
                  </div>
                )}
                                <div className="input-ferramentas">
                  <button className="btn-ferr" onClick={() => setShowEmoji(s => !s)}>😊</button>
                  <button className="btn-ferr" title="Respostas rápidas" onClick={() => setShowRespostasRapidas(s => !s)}>💬</button>
                  <button className="btn-ferr" onClick={() => fileInputRef.current?.click()}>📎</button>
                  <button
                    className={`btn-ferr ${gravando ? 'gravando' : ''}`}
                    onClick={gravando ? pararGravacao : iniciarGravacao}
                    title={gravando ? 'Parar gravação' : 'Gravar áudio'}
                  >{gravando ? '⏹️' : '🎤'}</button>
                  <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={enviarArquivo} />
                </div>
                <div className="input-row">
                  <textarea className="chat-textarea" value={editandoMsg ? textoEdicao : texto}
                    onChange={e => editandoMsg ? setTextoEdicao(e.target.value) : setTexto(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); editandoMsg ? editarMensagem() : enviarMensagem(); } if (e.key === 'Escape' && editandoMsg) { setEditandoMsg(null); setTextoEdicao(''); } }}
                    onPaste={e => {
                      const items = e.clipboardData?.items;
                      if (!items) return;
                      for (const item of items) {
                        if (item.type.startsWith('image/')) {
                          e.preventDefault();
                          e.stopPropagation();
                          const file = item.getAsFile();
                          if (!file || !atendimentoAtivo || atendimentoAtivo.status === 'encerrado') return;
                          const url = URL.createObjectURL(file);
                          editorShapesRef.current = [];
                          editorImgRef.current = null;
                          setEditorFerramenta('retangulo');
                          setEditorCor('#e53935');
                          setEditorTextoPos(null);
                          setEditorTextoInput('');
                          setImagemParaEditar({ arquivo: new File([file], 'captura.png', { type: 'image/png' }), url });
                          return;
                        }
                      }
                    }}
                    placeholder="Digite uma mensagem ou cole uma imagem (Ctrl+V)..." rows={1} />
                  <button className="btn-enviar" onClick={editandoMsg ? editarMensagem : enviarMensagem} disabled={(!editandoMsg && !texto.trim()) || enviando}>{editandoMsg ? '✓' : '➤'}</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Menu de contexto */}
      {menuContexto && (
        <>
        <div style={{position:'fixed',inset:0,zIndex:9998}} onClick={() => setMenuContexto(null)} />
        <div style={{
          position:'fixed', top:menuContexto.y, left:Math.min(menuContexto.x, window.innerWidth - 180),
          background:'white', borderRadius:8, boxShadow:'0 4px 16px rgba(0,0,0,0.15)',
          zIndex:9999, minWidth:140, overflow:'hidden'
        }}
          onClick={e => e.stopPropagation()}
        >
          <div style={{padding:'10px 16px', cursor:'pointer', fontSize:13, display:'flex', alignItems:'center', gap:8, color:'#222'}}
            onClick={() => { setRespondendo(menuContexto.msg); setMenuContexto(null); setTimeout(() => document.querySelector('.chat-textarea')?.focus(), 50); }}
            onMouseEnter={e => { e.currentTarget.style.background='var(--bg-hover)'; e.currentTarget.style.color='#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.background='transparent'; e.currentTarget.style.color='#222'; }}
          >
            ↩️ Responder
          </div>
          <div style={{padding:'10px 16px', cursor:'pointer', fontSize:13, display:'flex', alignItems:'center', gap:8, color:'#222'}}
            onClick={() => {
              const texto = menuContexto.msg?.conteudo || '';
              if (texto) navigator.clipboard.writeText(texto).catch(() => {
                const el = document.createElement('textarea');
                el.value = texto;
                document.body.appendChild(el);
                el.select();
                document.execCommand('copy');
                document.body.removeChild(el);
              });
              setMenuContexto(null);
            }}
            onMouseEnter={e => { e.currentTarget.style.background='var(--bg-hover)'; e.currentTarget.style.color='#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.background='transparent'; e.currentTarget.style.color='#222'; }}
          >
            📋 Copiar
          </div>
          <div style={{padding:'10px 16px', cursor:'pointer', fontSize:13, display:'flex', alignItems:'center', gap:8, color:'#222'}}
            onClick={async () => {
              const m = menuContexto.msg;
              setMenuContexto(null);
              if (!m?.id) { toast.error('Mensagem ainda sem identificador, tente de novo'); return; }
              try {
                await api.post('/anotacoes', { mensagem_id: m.id });
                toast.success('📝 Enviado para anotações'); window.dispatchEvent(new Event('anotacoes:atualizadas'));
              } catch (e) {
                toast.error(e.response?.data?.erro || 'Erro ao criar anotação');
              }
            }}
            onMouseEnter={e => { e.currentTarget.style.background='var(--bg-hover)'; e.currentTarget.style.color='#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.background='transparent'; e.currentTarget.style.color='#222'; }}
          >
            📝 Enviar para anotações
          </div>
          {menuContexto?.msg?.remetente === 'atendente' && menuContexto?.msg?.remetente_id === usuario?.id && menuContexto?.msg?.tipo === 'texto' && (
            <div style={{padding:'10px 16px', cursor:'pointer', fontSize:13, display:'flex', alignItems:'center', gap:8, color:'#222'}}
              onClick={() => {
                const msg = menuContexto.msg;
                menuContextoRef.current = null;
                setMenuContexto(null);
                editandoMsgRef.current = msg;
                setEditandoMsg(msg);
                setTextoEdicao(msg.conteudo || '');
                setTimeout(() => {
                  const ta = document.querySelector('.chat-textarea');
                  if (ta) { ta.focus(); ta.scrollIntoView({ behavior: 'smooth' }); }
                }, 100);
              }}
              onMouseEnter={e => { e.currentTarget.style.background='var(--bg-hover)'; e.currentTarget.style.color='#fff'; }}
              onMouseLeave={e => { e.currentTarget.style.background='transparent'; e.currentTarget.style.color='#222'; }}
            >
              ✏️ Editar
            </div>
          )}
          {menuContexto?.msg?.remetente === 'atendente' && menuContexto?.msg?.remetente_id === usuario?.id && (
            <div style={{padding:'10px 16px', cursor:'pointer', fontSize:13, display:'flex', alignItems:'center', gap:8, color:'#c0392b'}}
              onClick={async () => {
                const msg = menuContexto.msg;
                menuContextoRef.current = null;
                setMenuContexto(null);
                if (!window.confirm('Apagar esta mensagem?')) return;
                try {
                  await api.delete(`/mensagens/${msg.id}`);
                } catch(e) {
                  toast.error('Erro ao apagar mensagem');
                }
              }}
              onMouseEnter={e => { e.currentTarget.style.background='#e53935'; e.currentTarget.style.color='#fff'; }}
              onMouseLeave={e => { e.currentTarget.style.background='transparent'; e.currentTarget.style.color='#c0392b'; }}
            >
              🗑️ Apagar
            </div>
          )}

        </div>
        </>
      )}


      {imagemParaEditar && (
        <EditorImagem
          url={imagemParaEditar.url}
          arquivo={imagemParaEditar.arquivo}
          ferramenta={editorFerramenta}
          setFerramenta={setEditorFerramenta}
          cor={editorCor}
          setCor={setEditorCor}
          textoInput={editorTextoInput}
          setTextoInput={setEditorTextoInput}
          textoPos={editorTextoPos}
          setTextoPos={setEditorTextoPos}
          canvasRef={canvasEditorRef}
          drawingRef={editorDrawingRef}
          startRef={editorStartRef}
          shapesRef={editorShapesRef}
          imgRef={editorImgRef}
          onCancelar={() => { URL.revokeObjectURL(imagemParaEditar.url); setImagemParaEditar(null); }}
          onEnviar={async (blob) => {
            setImagemParaEditar(null);
            const file = new File([blob], imagemParaEditar.arquivo.name, { type: 'image/png' });
            await enviarArquivoDirecto(file);
          }}
        />
      )}
      {transferirModal && (
        <div className="modal-overlay" onClick={() => setTransferirModal(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Transferir atendimento</h3>
              <button onClick={() => setTransferirModal(false)}>✕</button>
            </div>
            <p className="modal-sub">Selecione o atendente:</p>
            {atendentes.length === 0 && (
              <div style={{padding:'16px 0', color:'var(--text-muted)', fontSize:13, textAlign:'center'}}>
                Nenhum atendente disponível neste canal
              </div>
            )}
            {atendentes.map(at => (
              <div key={at.id} className="atendente-opcao" onClick={() => fazerTransferencia(at.id)}>
                <div className="at-avatar">{at.nome.slice(0, 2).toUpperCase()}</div>
                <div>
                  <div className="at-nome">{at.nome}</div>
                  <div className="at-canais">
                    {at.canais?.length > 0
                      ? at.canais.map(c => c.nome).join(', ')
                      : 'Sem canais vinculados'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    {modalNovaConversa && (
      <div className="modal-overlay" onClick={() => setModalNovaConversa(false)}>
        <div className="modal-box" onClick={e => e.stopPropagation()} style={{width:360}}>
          <div className="modal-header">
            <h3>Nova Conversa</h3>
            <button onClick={() => setModalNovaConversa(false)}>✕</button>
          </div>
          <div style={{padding:'16px 20px',display:'flex',flexDirection:'column',gap:12}}>
            <div className="form-group">
              <label>Nome do cliente</label>
              <input type="text" placeholder="Ex: João Silva"
                value={novaConversa.nome}
                onChange={e => setNovaConversa(p => ({...p, nome: e.target.value}))} />
            </div>
            <div className="form-group">
              <label>Número do WhatsApp</label>
              <input type="text" placeholder="Ex: 47999990001"
                value={novaConversa.numero}
                onChange={e => setNovaConversa(p => ({...p, numero: e.target.value}))}
                onKeyDown={e => e.key === 'Enter' && iniciarConversa()} />
              <span className="hint">DDD + número, sem espaços ou traços</span>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn-secondary" onClick={() => setModalNovaConversa(false)}>Cancelar</button>
            <button className="btn-primary" onClick={iniciarConversa} disabled={iniciando}>
              {iniciando ? 'Iniciando...' : '💬 Iniciar Conversa'}
            </button>
          </div>
        </div>
      </div>
    )}
    </div>
  );
}
